import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { maskitoNumberOptionsGenerator, maskitoParseNumber } from '@maskito/kit';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';

@Component({
  selector: 'app-currency-input',
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CurrencyInputComponent implements ControlValueAccessor {
  protected cdr = inject(ChangeDetectorRef);

  @Input() placeholder: string = '';
  @Input() error: boolean = false;
  @Input() controlId: string | null = null;
  @Output() valueChange = new EventEmitter<number | null>();

  private _allowNegative = false;

  @Input()
  public set allowNegative(value: boolean) {
    this._allowNegative = value;
    this.mask = this.buildMask();
  }

  public mask = this.buildMask();

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: number | null): void {
    this.value = value === null ? '' : maskitoTransform(value?.toFixed(2), this.mask);
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;

    const numericValue = maskitoParseNumber(inputValue);
    this.valueChange.emit(isNaN(numericValue) ? null : numericValue);
    this.onChange(isNaN(numericValue) ? null : numericValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const numericValue = maskitoParseNumber(this.value);
    if (!isNaN(numericValue)) {
      const formattedValue = numericValue.toFixed(2);
      this.value = maskitoTransform(formattedValue, this.mask);
    } else {
      this.value = '';
    }
  }

  private buildMask(): MaskitoOptions {
    return maskitoNumberOptionsGenerator({
      precision: 2,
      thousandSeparator: ',',
      decimalSeparator: '.',
      ...(this._allowNegative ? {} : { min: 0 })
    });
  }
}
